import { useEffect, useState } from "react";
import axios from "axios";
import Reveal from "@/components/Reveal";
import { ArrowUpRight, Books } from "@phosphor-icons/react";
import { fmtPrecio } from "@/pages/LibreriaPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LibreriaTeaser = () => {
  const [books, setBooks] = useState(null);

  useEffect(() => {
    axios.get(`${API}/books`).then(({ data }) => setBooks(data)).catch(() => setBooks([]));
  }, []);

  if (books === null) return null;

  // Sin libros aún: banner simple invitando a la sección
  if (books.length === 0) {
    return (
      <section className="border-t border-white/10 bg-ink py-20 lg:py-24" data-testid="libreria-teaser">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <Reveal>
            <a href="/libreria" className="group flex flex-wrap items-center justify-between gap-8" data-testid="libreria-teaser-link">
              <div className="flex items-center gap-8">
                <span className="flex h-16 w-16 shrink-0 items-center justify-center border border-fire/40 text-fire transition-colors duration-300 group-hover:bg-fire group-hover:text-ink">
                  <Books size={28} weight="light" />
                </span>
                <div>
                  <p className="text-[10px] font-light uppercase tracking-[0.4em] text-fire">Librería · Apoya la obra</p>
                  <p className="mt-2 font-serif text-4xl leading-none text-paper transition-colors duration-300 group-hover:text-fire sm:text-5xl">
                    Librería <em className="italic text-gold group-hover:text-paper">Viento de Gracia</em>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <p className="max-w-xs text-sm font-light leading-relaxed text-smoke">
                  Libros cristianos seleccionados por nuestro equipo pastoral. Cada compra sostiene los ministerios de la casa.
                </p>
                <ArrowUpRight size={26} weight="light" className="shrink-0 text-smoke transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-fire" />
              </div>
            </a>
          </Reveal>
        </div>
      </section>
    );
  }

  const destacados = books.slice(0, 4);

  return (
    <section className="border-t border-white/10 bg-ink py-20 lg:py-28" data-testid="libreria-teaser">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="flex items-center gap-4 text-[10px] font-light uppercase tracking-[0.5em] text-fire">
                <span className="inline-block h-px w-16 bg-fire" /> Librería · Apoya la obra
              </p>
              <h2 className="mt-6 font-serif text-5xl leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl" data-testid="libreria-teaser-title">
                Libros que <em className="italic text-gold">edifican</em>
              </h2>
            </div>
            <a href="/libreria" className="group flex items-center gap-3 border border-white/15 px-6 py-3 text-[10px] font-light uppercase tracking-[0.25em] text-paper/70 transition-colors duration-300 hover:border-fire hover:text-fire" data-testid="libreria-teaser-cta">
              Ver todo el catálogo
              <ArrowUpRight size={16} weight="light" className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="libreria-teaser-grid">
          {destacados.map((b) => (
            <a key={b.id} href={`/libreria/${b.slug}`} className="group relative bg-ink" data-testid={`teaser-book-${b.slug}`}>
              <div className="overflow-hidden">
                <img
                  src={`${BACKEND_URL}${b.portada}`}
                  alt={`Portada del libro ${b.titulo}`}
                  className={`aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05] ${b.stock <= 0 ? "opacity-40 grayscale" : ""}`}
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <p className="font-serif text-lg leading-snug text-paper transition-colors duration-300 group-hover:text-fire">{b.titulo}</p>
                <div className="mt-3 flex items-center justify-between">
                  {b.precio_oferta > 0 && b.precio_oferta < b.precio ? (
                    <span className="flex items-center gap-2">
                      <span className="font-serif text-fire">{fmtPrecio(b.precio_oferta)}</span>
                      <span className="text-xs font-light text-smoke line-through">{fmtPrecio(b.precio)}</span>
                    </span>
                  ) : (
                    <span className="font-serif text-fire">{fmtPrecio(b.precio)}</span>
                  )}
                  <span className="text-[9px] font-light uppercase tracking-[0.25em] text-smoke">{b.stock > 0 ? "Disponible" : "Agotado"}</span>
                </div>
              </div>
            </a>
          ))}
        </div>

        <Reveal>
          <p className="mt-8 text-center text-xs font-light tracking-wide text-smoke" data-testid="libreria-teaser-nota">
            Compra con tarjeta en el sitio o aparta por WhatsApp · Recoge en la iglesia o pide envío a domicilio
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default LibreriaTeaser;
